import { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import axios from 'axios'
import './FriendChat.css'

export default function FriendChat({ gameId, gameName, currentUser }) {
  const [messages, setMessages] = useState([])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const messagesEndRef = useRef(null)
  const messagesContainerRef = useRef(null)
  const isUserAtBottomRef = useRef(false)  // Only updated by user scroll events
  const justSentMessageRef = useRef(false)
  const isInitialLoadRef = useRef(true)
  const isProgrammaticScrollRef = useRef(false)  // Ignore code-triggered scrolls
  const prevMessageCountRef = useRef(0)  // Track message count to detect new messages

  useEffect(() => {
    loadMessages()
    const interval = setInterval(loadMessages, 3000) // Poll for new messages
    return () => clearInterval(interval)
  }, [gameId])

  useEffect(() => {
    const prevCount = prevMessageCountRef.current
    const newCount = messages.length
    const hasNewMessages = newCount > prevCount

    console.log('[FriendChat][Scroll] messages useEffect triggered', {
      prevCount,
      newCount,
      hasNewMessages,
      isInitialLoad: isInitialLoadRef.current,
      isUserAtBottom: isUserAtBottomRef.current,
      justSentMessage: justSentMessageRef.current
    })

    // Update previous count
    prevMessageCountRef.current = newCount

    // Initial load: scroll to bottom once
    if (isInitialLoadRef.current && messages.length > 0) {
      console.log('[FriendChat][Scroll] INITIAL LOAD - scrolling to bottom')
      scrollToBottom()
      isInitialLoadRef.current = false
      return
    }

    // Only process if there are actually NEW messages
    if (!hasNewMessages) {
      console.log('[FriendChat][Scroll] NO NEW MESSAGES - skipping scroll logic')
      return
    }

    // Only auto-scroll if user manually scrolled to bottom OR just sent message
    if (isUserAtBottomRef.current || justSentMessageRef.current) {
      console.log('[FriendChat][Scroll] AUTO-SCROLL triggered', {
        reason: justSentMessageRef.current ? 'just sent message' : 'user at bottom'
      })
      scrollToBottom()
      justSentMessageRef.current = false
    } else {
      console.log('[FriendChat][Scroll] NEW MESSAGES but user not at bottom - no scroll')
    }
  }, [messages])

  const scrollToBottom = () => {
    console.log('[FriendChat][Scroll] scrollToBottom() called - setting isProgrammaticScroll=true')
    isProgrammaticScrollRef.current = true
    // Use scrollTo on container instead of scrollIntoView to avoid scrolling the whole page
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTo({
        top: messagesContainerRef.current.scrollHeight,
        behavior: 'smooth'
      })
    }
    // Reset flag after animation completes (~300ms smooth scroll)
    setTimeout(() => {
      console.log('[FriendChat][Scroll] setTimeout fired - setting isProgrammaticScroll=false')
      isProgrammaticScrollRef.current = false
    }, 400)
  }

  const handleScroll = () => {
    const isProgrammatic = isProgrammaticScrollRef.current

    // Ignore ALL scroll events during programmatic scroll animation
    if (isProgrammatic) {
      console.log('[FriendChat][Scroll] handleScroll IGNORED (programmatic scroll in progress)')
      return
    }

    if (!messagesContainerRef.current) return
    const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight
    const wasAtBottom = isUserAtBottomRef.current
    // Consider "at bottom" if within 50px of the bottom
    isUserAtBottomRef.current = distanceFromBottom < 50

    console.log('[FriendChat][Scroll] handleScroll USER EVENT', {
      scrollTop,
      scrollHeight,
      clientHeight,
      distanceFromBottom,
      wasAtBottom,
      nowAtBottom: isUserAtBottomRef.current
    })
  }

  const loadMessages = async () => {
    try {
      const response = await axios.get(`/api/chats/game/${gameId}`, {
        withCredentials: true
      })
      const newMessages = response.data

      // Only update state if messages actually changed (avoid unnecessary re-renders)
      setMessages(prevMessages => {
        // Compare length first (fast check)
        if (prevMessages.length !== newMessages.length) {
          return newMessages
        }

        // If lengths match, check if content changed (compare last message ID)
        if (newMessages.length > 0 && prevMessages.length > 0) {
          const lastPrevId = prevMessages[prevMessages.length - 1].chat_id
          const lastNewId = newMessages[newMessages.length - 1].chat_id
          if (lastPrevId !== lastNewId) {
            return newMessages
          }
        }

        // No change detected - return previous state to avoid re-render
        return prevMessages
      })
    } catch (error) {
      // Don't log network suspension (expected when tab is in background)
      if (error.code !== 'ERR_NETWORK') {
        console.error('Failed to load messages:', error)
      }
    }
  }

  const sendMessage = async (e) => {
    e.preventDefault()
    if (!input.trim()) return

    const messageText = input.trim()
    setInput('')
    setLoading(true)
    justSentMessageRef.current = true // Auto-scroll to show Ocean's message

    try {
      await axios.post(
        `/api/chats/game/${gameId}`,
        { message: messageText, game_name: gameName },
        { withCredentials: true }
      )
      await loadMessages() // Reload to get new message
    } catch (error) {
      console.error('Failed to send message:', error)
    } finally {
      setLoading(false)
    }
  }

  // Check if user has no friends
  const hasFriends = currentUser.friend_count > 0

  return (
    <div className="friend-chat">
      <div className="friend-chat-header">
        <div className="friend-chat-title">
          <span className="chat-icon">👥</span>
          <span>Friends Chat</span>
        </div>
        <div className="friend-chat-subtitle">{gameName}</div>
      </div>

      <div
        className="friend-messages"
        ref={messagesContainerRef}
        onScroll={handleScroll}
      >
        <AnimatePresence initial={false}>
          {!hasFriends ? (
            <motion.div
              className="friend-welcome no-friends"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="welcome-icon">👥</div>
              <h3>No Friends Yet!</h3>
              <p>Add friends to chat about games together. Share your thoughts, get recommendations, and compare reviews!</p>
              <p className="friend-hint">💡 Tip: Ask your gaming buddies to join OceanDaniel.com!</p>
            </motion.div>
          ) : messages.length === 0 ? (
            <motion.div
              className="friend-welcome"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="welcome-icon">💬</div>
              <p>Be the first to start the conversation about {gameName}!</p>
            </motion.div>
          ) : null}

          {hasFriends && messages.map((msg, index) => {
            const isCurrentUser = msg.user_id === currentUser.user_id
            return (
              <motion.div
                key={msg.chat_id}
                className={`friend-message ${isCurrentUser ? 'own' : 'other'}`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.03 }}
              >
                <div className="friend-message-header">
                  <img
                    src={msg.avatar_url || '/avatars/link.svg'}
                    alt={msg.display_name}
                    className="friend-avatar"
                  />
                  <span className="friend-name">{msg.display_name}</span>
                  <span className="friend-time">
                    {new Date(msg.created_at).toLocaleTimeString('en-US', {
                      hour: 'numeric',
                      minute: '2-digit'
                    })}
                  </span>
                </div>
                <div className="friend-message-bubble">
                  {msg.message_text}
                </div>
              </motion.div>
            )
          })}
        </AnimatePresence>
        <div ref={messagesEndRef} />
      </div>

      <form className="friend-input-form" onSubmit={sendMessage}>
        <input
          type="text"
          className="friend-input"
          placeholder={hasFriends ? "Type your message..." : "Add friends to start chatting..."}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          disabled={loading || !hasFriends}
        />
        <motion.button
          type="submit"
          className="friend-send-btn"
          disabled={loading || !input.trim() || !hasFriends}
          whileHover={{ scale: (!hasFriends || loading) ? 1 : 1.05 }}
          whileTap={{ scale: (!hasFriends || loading) ? 1 : 0.95 }}
        >
          Send
        </motion.button>
      </form>
    </div>
  )
}
